import React, { useId } from 'react'
import { Box, IconButton } from '@mui/material'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { TESTIDS } from '../../../../config/testids'
import type { PlatformTourStop } from '../platformTourStops'
import type { TourPanelPlacement } from '../hooks/useTourPanelPlacement'
import { PlatformTourStopList } from './PlatformTourStopList'
import { PlatformTourHighlights } from './PlatformTourHighlights'
import { PlatformTourStopFooter } from './PlatformTourStopFooter'
import {
  buildTourArrowSx,
  buildTourPanelSx,
  tourBodySx,
  tourCloseButtonSx,
  tourContentColumnSx,
  tourStopCounterSx,
  tourStopTitleSx
} from './platformTourStyles'

interface PlatformTourStopPanelProps {
  stops: PlatformTourStop[]
  stopIndex: number
  placement: TourPanelPlacement
  onBack: () => void
  onNext: () => void
  onSkip: () => void
  onPause: () => void
}

/** The panel beside the lit menu entry: the tour's stops, then what this page is for. */
export const PlatformTourStopPanel: React.FC<PlatformTourStopPanelProps> = ({
  stops,
  stopIndex,
  placement,
  onBack,
  onNext,
  onSkip,
  onPause
}) => {
  const { t } = useTranslation()
  const titleId = useId()
  const stop = stops[stopIndex]
  const nextStop = stops[stopIndex + 1]
  const isDocked = !placement.anchorRect

  const nextLabel = nextStop
    ? t('gamer.platformTour.nextStop', { stop: t(nextStop.labelKey) })
    : t('gamer.platformTour.finishTour')

  return (
    <Box
      ref={placement.panelRef}
      role="dialog"
      aria-modal
      aria-labelledby={titleId}
      sx={buildTourPanelSx(isDocked ? null : placement)}
      data-testid={TESTIDS.PLATFORM_TOUR_STOP_PANEL}
    >
      {!isDocked && <Box sx={buildTourArrowSx(placement.arrowTop)} aria-hidden />}
      {!isDocked && <PlatformTourStopList stops={stops} stopIndex={stopIndex} />}

      <Box sx={tourContentColumnSx}>
        <IconButton
          onClick={onPause}
          aria-label={t('gamer.platformTour.pauseTour')}
          sx={tourCloseButtonSx}
          data-testid={TESTIDS.PLATFORM_TOUR_PAUSE_BTN}
        >
          <XMarkIcon />
        </IconButton>

        <Box sx={tourStopCounterSx}>
          {t('gamer.platformTour.stopOf', { current: stopIndex + 1, total: stops.length })}
        </Box>
        <Box component="h2" id={titleId} sx={tourStopTitleSx}>
          {t(stop.labelKey)}
        </Box>
        <Box sx={tourBodySx}>{t(stop.descriptionKey)}</Box>

        <PlatformTourHighlights highlightKeys={stop.highlightKeys} />

        <PlatformTourStopFooter
          nextLabel={nextLabel}
          hasBack={stopIndex > 0}
          onSkip={onSkip}
          onBack={onBack}
          onNext={onNext}
        />
      </Box>
    </Box>
  )
}
